import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, CircleAlert, Info } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";

const TOAST_DURATION = 15_000;

const variantDetails = {
  success: { Icon: CheckCircle2, iconClass: "bg-emerald-600", role: "status", live: "polite" },
  error: { Icon: CircleAlert, iconClass: "bg-red-600", role: "alert", live: "assertive" },
  warning: { Icon: AlertTriangle, iconClass: "bg-amber-500", role: "alert", live: "assertive" },
  info: { Icon: Info, iconClass: "bg-cdi-blue", role: "status", live: "polite" },
  destructive: { Icon: CircleAlert, iconClass: "bg-red-600", role: "alert", live: "assertive" },
  default: { Icon: Info, iconClass: "bg-cdi-blue", role: "status", live: "polite" },
};

function ToastItem({ id, title, description, action, variant = "info", open, onDismiss, onOpenChange: _onOpenChange, ...props }) {
  const timeoutRef = useRef(null);
  const remainingRef = useRef(TOAST_DURATION);
  const startedAtRef = useRef(0);
  const pauseReasonsRef = useRef(new Set());
  const onDismissRef = useRef(onDismiss);
  const [paused, setPaused] = useState(false);
  const details = variantDetails[variant] || variantDetails.info;
  const { Icon } = details;

  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!open) return undefined;

    const startTimer = () => {
      startedAtRef.current = performance.now();
      timeoutRef.current = window.setTimeout(() => onDismissRef.current(id), remainingRef.current);
    };

    startTimer();
    return () => window.clearTimeout(timeoutRef.current);
  }, [id, open]);

  const pause = (reason) => {
    if (!open || pauseReasonsRef.current.has(reason)) return;
    pauseReasonsRef.current.add(reason);
    if (pauseReasonsRef.current.size > 1) return;

    remainingRef.current = Math.max(0, remainingRef.current - (performance.now() - startedAtRef.current));
    window.clearTimeout(timeoutRef.current);
    setPaused(true);
  };

  const resume = (reason) => {
    pauseReasonsRef.current.delete(reason);
    if (!open || pauseReasonsRef.current.size > 0) return;

    if (remainingRef.current <= 0) {
      onDismissRef.current(id);
      return;
    }
    startedAtRef.current = performance.now();
    timeoutRef.current = window.setTimeout(() => onDismissRef.current(id), remainingRef.current);
    setPaused(false);
  };

  return (
    <Toast
      {...props}
      variant={variant}
      open={open}
      role={details.role}
      aria-live={details.live}
      aria-atomic="true"
      onMouseEnter={() => pause("hover")}
      onMouseLeave={() => resume("hover")}
      onFocusCapture={() => pause("focus")}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) resume("focus");
      }}
    >
      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center text-white ${details.iconClass}`}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        {title && <ToastTitle>{title}</ToastTitle>}
        {description && <ToastDescription className={title ? "mt-1" : undefined}>{description}</ToastDescription>}
        {action}
      </div>
      <ToastClose aria-label="Fechar notificação" onClick={() => onDismiss(id)} />
      <div className="absolute inset-x-0 bottom-0 h-1 overflow-hidden bg-cdi-blue/10" aria-hidden="true">
        <span
          className={`block h-full origin-left toast-progress ${details.iconClass}`}
          style={{ animationDuration: `${TOAST_DURATION}ms`, animationPlayState: paused ? "paused" : "running" }}
        />
      </div>
    </Toast>
  );
}

export function Toaster() {
  const { toasts, dismiss } = useToast();

  return (
    <ToastProvider>
      <ToastViewport
        className={toasts.length ? "pointer-events-auto" : undefined}
        role="region"
        aria-label="Notificações"
      >
        {toasts.map((notification) => (
          <ToastItem key={notification.id} {...notification} onDismiss={dismiss} />
        ))}
      </ToastViewport>
    </ToastProvider>
  );
}
